import React, { useMemo, useCallback } from 'react';
import classnames from 'classnames';
import moment, { Moment } from 'moment';
import RangePicker from '@home/components/RangePicker';

import './index.scss';

interface GeneralRangePickerPropsType extends IBaseProps {
  showShortcut?: boolean;
  showSelectType?: boolean;
  type?: 'month';
  typeList?: string[];
  disabled?: boolean;
  disabledMonth?: ((date: Moment, startDate?: Moment) => boolean) | null;
  value?: [string, string] | string;
  onChange: ((value: any, type: string) => void) | null;
  isHideLeftDateSizeSelector?: boolean;
  isRange?: boolean; // 是否是跨度选，false为单选
  // 可选的日期范围（月筛选器要传起始月的第一天，结束月的最后一天。周筛选器等同理），不在可选范围的日期禁用
  // 这个属性不传时，或者传了无效的数据时，日期将全部可用
  dateRange?: [string, string] | null;
}

const DEFAULT_TYPES = ['month'];
const DEFAULT_VALUE = ['', ''];

const GeneralRangePicker: React.FC<GeneralRangePickerPropsType> = (props) => {
  const {
    style,
    className,
    showShortcut = true,
    showSelectType = false,
    type = 'month',
    typeList = DEFAULT_TYPES,
    disabled = false,
    disabledMonth = null,
    value = DEFAULT_VALUE,
    onChange = null,
    isHideLeftDateSizeSelector = true,
    isRange = true,
    dateRange = null,
  } = props;

  // 将value转成moment对象
  const dateValue = useMemo(() => {
    // 单选
    if (!isRange && typeof value === 'string' && moment(value).isValid()) {
      return [moment(value), moment(value)];
      // 跨度选
    }
    if (isRange && Array.isArray(value) && moment(value?.[0]).isValid() && moment(value?.[1]).isValid()) {
      return [moment(value?.[0]), moment(value?.[1])];
      // 无数据情况
    }
    return ['', ''];
  }, [value, isRange]);

  const handleChange = (v: any, type: string) => {
    // 将moment对象转换成字符串返回
    let realValue: any = null;
    // 跨度选
    if (isRange) {
      realValue = ['', ''];
      if (Array.isArray(v) && v?.[0] && v?.[1]) {
        try {
          // 月粒度时，需要把第二个日期调整为最后一天
          const endDate = type === 'month' ? v?.[1]?.endOf('month') : v?.[1];
          realValue = [v?.[0]?.format('YYYY-MM-DD'), endDate?.format('YYYY-MM-DD')];
          // 当不在可选范围时
          if (dateRange && moment(dateRange?.[0]).isValid() && moment(dateRange?.[1]).isValid()) {
            if (v?.[0]?.isBefore(dateRange?.[0])) {
              realValue[0] = dateRange?.[0];
            }
            if (endDate.isAfter(dateRange?.[1])) {
              realValue[1] = dateRange?.[1];
            }
          }
        } catch (error) {}
      }
      // 单选
    } else {
      realValue = '';
      try {
        realValue = v?.[1]?.format('YYYY-MM-DD') || '';
      } catch (error) {}
    }
    onChange?.(realValue, type);
  };

  // 不在可选范围内的日期禁用
  const disabledFun = useCallback(
    (date: Moment) => {
      if (dateRange && date && moment(dateRange?.[0]).isValid() && moment(dateRange?.[1]).isValid()) {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];
        if (date.isBefore(startDate) || date.isAfter(endDate)) {
          return false;
        }
        return true;
      }
      return true;
    },
    [dateRange?.[0], dateRange?.[1]],
  );

  return (
    <div
      style={style}
      className={classnames('dbg-common-range-picker search-item-date', className, {
        'dbg-common-range-picker-no-left-label': isHideLeftDateSizeSelector,
      })}
    >
      <RangePicker
        placeholder="Select Date"
        language="en"
        showSelectType={showSelectType}
        showShortcut={showShortcut && isRange}
        disabled={disabled}
        disabledMonth={disabledMonth || disabledFun}
        type={type}
        typeList={typeList}
        autoRange={
          isRange || {
            limeType: 'date',
            length: 0,
          }
        }
        value={dateValue}
        onChange={handleChange}
      />
    </div>
  );
};

export default React.memo(GeneralRangePicker);
